/**
 * ClaimEditorRow — Phase 5b affordance row attached under each claim in the
 * read-only PerspectivePanel.  Compact icon strip + inline-text editor.
 *
 * Affordances (5 of 7 surfaced inline; "unlock" toggles "lock"; manual_create
 * lives in a separate "+ Add claim" button at the panel level):
 *   • edit_text          (pencil → inline textarea)
 *   • lock / unlock      (lock icon toggle)
 *   • retract            (trash icon with confirm dialog)
 *   • resolve            (check icon with confirm dialog)
 *   • override_confidence (slider in expander)
 *   • override_decay     (number input in expander)
 */
import React, { useState } from "react";
import {
  Pencil, Lock, Unlock, Trash2, CheckCircle2, Circle, Sliders, X, Save,
} from "lucide-react";
import { authAxios } from "../../utils/auth";

async function patchClaim({ matchId, claimId, action, payload }) {
  try {
    const res = await authAxios.patch(
      `/relationship/${matchId}/perspective/${claimId}`,
      { action, payload }
    );
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    throw new Error(detail || `${action} failed${status ? ` (${status})` : ""}`);
  }
}

export default function ClaimEditorRow({ claim, matchId, onChange, isSelected = false, onToggleSelect }) {
  const [mode, setMode] = useState(null); // 'edit' | 'confidence' | 'decay' | null
  const [draft, setDraft] = useState(claim.claim_text || "");
  const [confDraft, setConfDraft] = useState(claim.confidence || 0.5);
  const [decayDraft, setDecayDraft] = useState(90);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function run(action, payload = {}) {
    setBusy(true);
    setError(null);
    try {
      await patchClaim({
        matchId, claimId: claim.claim_id, action, payload,
      });
      setMode(null);
      onChange && onChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-2 border-t border-white/5 pt-2"
      data-testid={`claim-editor-${claim.claim_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => { setDraft(claim.claim_text || ""); setMode("edit"); }}
          disabled={busy}
          className="text-white/40 hover:text-divine-gold disabled:opacity-30 p-1"
          title="Edit text"
          data-testid={`claim-edit-${claim.claim_id}`}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => run(claim.user_locked ? "unlock" : "lock")}
          disabled={busy}
          className="text-white/40 hover:text-divine-gold disabled:opacity-30 p-1"
          title={claim.user_locked ? "Unlock (allow auto decay)" : "Lock (freeze)"}
          data-testid={`claim-lock-${claim.claim_id}`}
        >
          {claim.user_locked ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
        </button>
        <button
          onClick={() => setMode("confidence")}
          disabled={busy}
          className="text-white/40 hover:text-divine-gold disabled:opacity-30 p-1"
          title="Override confidence"
          data-testid={`claim-confidence-${claim.claim_id}`}
        >
          <Sliders className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setMode("decay")}
          disabled={busy}
          className="text-white/40 hover:text-divine-gold disabled:opacity-30 p-1 text-[10px] tracking-wide"
          title="Override decay window"
          data-testid={`claim-decay-${claim.claim_id}`}
        >
          DECAY
        </button>
        <div className="flex-1" />
        {/* Resolve selection checkbox (Option C — gold ring → emerald
            filled check). Replaces the legacy single-click resolve
            affordance; commit happens via the bulk RESOLVE button at
            the top of PerspectivePanel. */}
        <button
          type="button"
          onClick={() => onToggleSelect && onToggleSelect(claim.claim_id)}
          disabled={busy || !onToggleSelect}
          aria-pressed={isSelected}
          className={`p-1 rounded-full transition-all duration-150 ${
            isSelected
              ? "text-emerald-300 drop-shadow-[0_0_4px_rgba(16,185,129,0.6)]"
              : "text-divine-gold/40 hover:text-divine-gold/90 hover:bg-divine-gold/5"
          } disabled:opacity-30`}
          title={isSelected ? "Selected — click to deselect" : "Select to resolve"}
          data-testid={`claim-resolve-${claim.claim_id}`}
        >
          {isSelected ? (
            <CheckCircle2 className="w-4 h-4 fill-emerald-500/90 stroke-white" strokeWidth={2.5} />
          ) : (
            <Circle className="w-4 h-4" strokeWidth={2} />
          )}
        </button>
        <button
          onClick={() => {
            if (window.confirm("Retract this claim? It will be hidden from the perspective.")) {
              run("retract", { reason: "User retracted" });
            }
          }}
          disabled={busy}
          className="text-rose-400/60 hover:text-rose-300 disabled:opacity-30 p-1"
          title="Retract"
          data-testid={`claim-retract-${claim.claim_id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {mode === "edit" ? (
        <div className="mt-2 space-y-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            className="w-full bg-black/40 border border-divine-gold/30 rounded p-2 text-white text-xs"
            data-testid={`claim-edit-textarea-${claim.claim_id}`}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setMode(null)}
              className="text-white/50 hover:text-white text-xs p-1"
            >
              <X className="w-3 h-3 inline" /> Cancel
            </button>
            <button
              onClick={() => run("edit_text", { claim_text: draft })}
              disabled={busy || !draft.trim()}
              className="bg-divine-gold/20 hover:bg-divine-gold/30 text-divine-gold text-xs px-2 py-1 rounded disabled:opacity-30"
              data-testid={`claim-edit-save-${claim.claim_id}`}
            >
              <Save className="w-3 h-3 inline" /> Save
            </button>
          </div>
        </div>
      ) : null}

      {mode === "confidence" ? (
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-2">
            <input
              type="range" min="0" max="1" step="0.05"
              value={confDraft}
              onChange={(e) => setConfDraft(parseFloat(e.target.value))}
              className="flex-1"
              data-testid={`claim-confidence-slider-${claim.claim_id}`}
            />
            <span className="text-divine-gold text-xs w-10 text-right">
              {(confDraft * 100).toFixed(0)}%
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setMode(null)} className="text-white/50 text-xs p-1">Cancel</button>
            <button
              onClick={() => run("override_confidence", { confidence: confDraft })}
              className="bg-divine-gold/20 text-divine-gold text-xs px-2 py-1 rounded"
              data-testid={`claim-confidence-save-${claim.claim_id}`}
            >
              Save
            </button>
          </div>
        </div>
      ) : null}

      {mode === "decay" ? (
        <div className="mt-2 space-y-1">
          <label className="text-white/50 text-[10px]">
            Decay after N days (1-3650):
            <input
              type="number" min="1" max="3650"
              value={decayDraft}
              onChange={(e) => setDecayDraft(parseInt(e.target.value || "0", 10))}
              className="ml-2 bg-black/40 border border-divine-gold/30 rounded px-2 py-1 text-white text-xs w-24"
              data-testid={`claim-decay-input-${claim.claim_id}`}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={() => setMode(null)} className="text-white/50 text-xs p-1">Cancel</button>
            <button
              onClick={() => run("override_decay", { decay_after_days: decayDraft })}
              disabled={!decayDraft}
              className="bg-divine-gold/20 text-divine-gold text-xs px-2 py-1 rounded disabled:opacity-30"
              data-testid={`claim-decay-save-${claim.claim_id}`}
            >
              Save
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mt-1 text-rose-300 text-[10px]" data-testid={`claim-error-${claim.claim_id}`}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
